import { ref } from 'vue'
import { initFirebase } from '../firebase'
import { signInAnonymously } from 'firebase/auth'
import { doc, setDoc, serverTimestamp, collection, getDocs, query, orderBy, getDoc, limit, startAfter } from 'firebase/firestore'
import { UserModel } from '../models/UserModel'

const { db, auth } = initFirebase()

export class FirebaseUserService {
  constructor() {
    this.user = ref(new UserModel())
    this.isNewUser = ref(true)
    this.loadUserFromLocalStorage()
  }

  loadUserFromLocalStorage() {
    const savedUser = localStorage.getItem('userModel')
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser)
        this.user.value = new UserModel(userData)
        this.isNewUser.value = false
      } catch (error) {
        console.error('Error loading user from localStorage:', error)
      }
    }
  }

  async signInAndSaveUser(userData) {
    try {
      const userCredential = await signInAnonymously(auth)
      const firebaseUser = userCredential.user
      
      if (!firebaseUser) {
        throw new Error('No user returned from anonymous sign in')
      }

      const userModel = new UserModel({
        uid: firebaseUser.uid,
        createdAt: serverTimestamp(),
        properties: {
          name: userData.name,
          date: userData.birthDate,
          month: userData.birthMonth,
          year: userData.birthYear,
          hours: userData.birthTime,
          minutes: userData.birthMinutes,
          seconds: 0,
          address: userData.birthLocation?.name,
          latitude: userData.birthLocation?.lat,
          longitude: userData.birthLocation?.lng,
          gender: userData.gender,
          guessCountry: userData.birthLocation?.name?.split(',').pop()?.trim() || 'unknown',
          familiarity: '',
        }
      })

      await this.saveUserToFirestore(userModel)
      return userModel
    } catch (error) {
      console.error('Authentication error:', error)
      throw error
    }
  }

  async saveUserToFirestore(user) {
    try {
      const userRef = doc(db, 'users', user.uid)

      const properties = {  
        date: user.properties.date,
        month: user.properties.month,
        timezone: user.properties.timezone,
        year: user.properties.year,
        hours: user.properties.hours,
        minutes: user.properties.minutes,
        seconds: user.properties.seconds,
        guessCountry: user.properties.guessCountry || 'unknown',
        gender: user.properties.gender || 'prefer_not_to_say',
        name: user.properties.name,
        familiarity: user.properties.familiarity || '',
        isWeb: true
      }

      if (user.properties.address && user.properties.address.length > 0) {
        properties.address = user.properties.address
      }

      if (user.properties.longitude !== null) {
        properties.longitude = user.properties.longitude
      }

      if (user.properties.latitude !== null) {
        properties.latitude = user.properties.latitude
      }

      const userData = {
        uid: user.uid,
        properties: properties,
        createdAt: serverTimestamp()
      }

      await setDoc(userRef, userData, { merge: true })
      this.saveUserToLocalStorage(user)
      this.user.value = user
      return true
    } catch (error) {
      console.error('Error saving user to Firestore:', error)
      throw error
    }
  }

  saveUserToLocalStorage(user) {
    try {
      localStorage.setItem('userModel', JSON.stringify(user))
    } catch (error) {
      console.error('Error saving user to localStorage:', error)
    }
  }

  async logoutUser() {
    try {
      localStorage.removeItem('userModel')
      localStorage.removeItem('dont_know_birth_date')
      localStorage.removeItem('dont_know_birth_location')
      localStorage.removeItem('push_notifications_enabled')
      localStorage.removeItem('daily_updates_enabled')
      localStorage.removeItem('weekly_digest_enabled')
      localStorage.removeItem('sky_alerts_enabled')
      localStorage.removeItem('news_updates_enabled')
      
      if (auth.currentUser) {
        await auth.signOut()
      }
      
      this.user.value = new UserModel()
      this.isNewUser.value = true
    } catch (error) {
      console.error('Error during logout:', error)
      // Hata durumunda bile localStorage'ı temizle
      localStorage.clear()
      this.user.value = new UserModel()
      this.isNewUser.value = true
      throw error
    }
  }

  async getAllUsers() {
    try {
      const usersRef = collection(db, 'users')
      const q = query(usersRef, orderBy('createdAt', 'desc'))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }))
    } catch (error) {
      console.error('Error fetching users:', error)
      throw error
    }
  }

  async getUsersPaginated(pageSize = 40, lastDoc = null) {
    try {
      const usersRef = collection(db, 'users')
      let q
      
      if (lastDoc) {
        q = query(usersRef, orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize))
      } else {
        q = query(usersRef, orderBy('createdAt', 'desc'), limit(pageSize))
      }
      
      const snap = await getDocs(q)
      const users = snap.docs.map(d => ({ uid: d.id, ...d.data() }))
      const lastVisible = snap.docs[snap.docs.length - 1]
      
      return {
        users,
        lastVisible,
        hasMore: snap.docs.length === pageSize
      }
    } catch (error) {
      console.error('Error fetching users paginated:', error)
      throw error
    }
  }

  async searchUserByUid(uid) {
    try {
      if (!uid || uid.length < 3) {
        return {
          users: [],
          lastVisible: null,
          hasMore: false,
          isSearchResult: true
        }
      }

      const userDoc = await getDoc(doc(db, 'users', uid))
      if (userDoc.exists()) {
        return {
          users: [{ uid: userDoc.id, ...userDoc.data() }],
          lastVisible: null,
          hasMore: false,
          isSearchResult: true
        }
      }

      return {
        users: [],
        lastVisible: null,
        hasMore: false,
        isSearchResult: true
      }
    } catch (error) {
      console.error('Error searching user by UID:', error)
      return {
        users: [],
        lastVisible: null,
        hasMore: false,
        isSearchResult: true
      }
    }
  }

  async searchUserByName(name, pageSize = 100) {
    try {
      if (!name || name.length < 2) {
        return {
          users: [],
          lastVisible: null,
          hasMore: false,
          isSearchResult: true
        }
      }

      const usersRef = collection(db, 'users')
      
      const allSnap = await getDocs(query(usersRef, orderBy('createdAt', 'desc')))
      const allUsers = allSnap.docs.map(d => ({ uid: d.id, ...d.data() }))
      
      const searchLower = name.toLowerCase()
      const filtered = allUsers.filter(user => {
        const userName = (user.properties?.name || '').toLowerCase()
        return userName.includes(searchLower)
      })
      
      return {
        users: filtered.slice(0, pageSize),
        lastVisible: null,
        hasMore: false,
        isSearchResult: true
      }
    } catch (error) {
      console.error('Error searching user by name:', error)
      throw error
    }
  }

  async getUserById(userId) {
    try {
      const userRef = doc(db, 'users', userId)
      const userDoc = await getDoc(userRef)
      if (userDoc.exists()) {
        return {
          uid: userDoc.id,
          ...userDoc.data()
        }
      }
      return null
    } catch (error) {
      console.error('Error fetching user:', error)
      throw error
    }
  }

  async updateUser(uid, data) {
    try {
      const userRef = doc(db, 'users', uid)
      await setDoc(userRef, data, { merge: true })
      return true
    } catch (error) {
      console.error('Error updating user:', error)
      throw error
    }
  }

  // async deleteUser(uid) {
  //   try {
  //     const userRef = doc(db, 'users', uid)
  //     await deleteDoc(userRef)
  //     return true
  //   } catch (error) {
  //     console.error('Error deleting user:', error)
  //     throw error
  //   }
  // }
} 